import React, { useState, useEffect } from 'react';
import { MessageSquare, Users, Calculator, FileText, Loader2, Lock, Check, X, Search, Building, MapPin, Phone, Mail } from 'lucide-react';
// REMOVIDO: firebase/functions
// ADICIONADO: Axios
import api from '../services/api'; 
// OBS: Certifique-se de que ../services/api está configurado apontando para seu servidor

// --- HELPER: VALIDAÇÃO CPF/CNPJ (Atualizado para Alfanumérico) ---
const validateCpfCnpj = (val) => {
    if (!val) return false;
    // Permite números e letras (A-Z) para CNPJ
    const clean = val.replace(/[^\dA-Za-z]/gi, '').toUpperCase();
    if (clean.length === 11) {
        if (!/^\d{11}$/.test(clean)) return false;
        if (/^(\d)\1+$/.test(clean)) return false;
        let sum = 0, remainder;
        for (let i = 1; i <= 9; i++) sum += parseInt(clean.substring(i - 1, i)) * (11 - i);
        remainder = (sum * 10) % 11;
        if ((remainder === 10) || (remainder === 11)) remainder = 0;
        if (remainder !== parseInt(clean.substring(9, 10))) return false;
        sum = 0;
        for (let i = 1; i <= 10; i++) sum += parseInt(clean.substring(i - 1, i)) * (12 - i);
        remainder = (sum * 10) % 11;
        if ((remainder === 10) || (remainder === 11)) remainder = 0;
        if (remainder !== parseInt(clean.substring(10, 11))) return false;
        return true;
    } else if (clean.length === 14) {
        if (/^([A-Z0-9])\1+$/.test(clean)) return false;
        let size = clean.length - 2;
        let numbers = clean.substring(0, size);
        const digits = clean.substring(size);
        
        const calcCheckDigit = (str, initialPos) => {
            let sum = 0;
            let pos = initialPos;
            for (let i = 0; i < str.length; i++) {
                const char = str.charAt(i);
                const val = (char >= '0' && char <= '9') ? parseInt(char, 10) : char.charCodeAt(0) - 48;
                sum += val * pos--;
                if (pos < 2) pos = 9;
            }
            const remainder = sum % 11;
            return remainder < 2 ? 0 : 11 - remainder;
        };

        const result1 = calcCheckDigit(numbers, size - 7);
        if (result1 !== parseInt(digits.charAt(0), 10)) return false;
        
        size = size + 1;
        numbers = clean.substring(0, size);
        const result2 = calcCheckDigit(numbers, size - 7);
        if (result2 !== parseInt(digits.charAt(1), 10)) return false;
        return true;
    }
    return false;
};

const SignUpScreen = ({ setView }) => {
    // Configuração de Preço Dinâmico
    const BASE_USERS = 3;
    const BASE_PRICE = 199.90;
    const EXTRA_USER_PRICE = 99.90;

    const [formData, setFormData] = useState({
        tipo_pessoa: 'PJ',
        companyName: '',
        cnpj: '',
        cpf: '',
        ie: '',
        financial_email: '',
        phone: '',
        address: {
            cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: ''
        },
        adminName: '',
        adminEmail: '',
        adminPassword: '', 
        usersLimit: 3
    });

    const [validations, setValidations] = useState({
        doc: null,
        email: null,
        password: null
    });

    const [cnpjLoading, setCnpjLoading] = useState(false);
    const [cnpjMsg, setCnpjMsg] = useState(null);

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // --- PREÇO AUTOMÁTICO ---
    const totalPrice = React.useMemo(() => {
        const extraUsers = Math.max(0, formData.usersLimit - BASE_USERS);
        return BASE_PRICE + (extraUsers * EXTRA_USER_PRICE);
    }, [formData.usersLimit]);

    // --- VALIDAÇÃO EM TEMPO REAL ---
    useEffect(() => {
        const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail);
        const docToValidate = formData.tipo_pessoa === 'PJ' ? formData.cnpj : formData.cpf;
        const docValid = validateCpfCnpj(docToValidate);
        const passValid = formData.adminPassword.length >= 6;

        setValidations({
            email: formData.adminEmail ? emailValid : null,
            doc: docToValidate ? docValid : null,
            password: formData.adminPassword ? passValid : null
        });
    }, [formData.adminEmail, formData.cnpj, formData.cpf, formData.tipo_pessoa, formData.adminPassword]);

    // --- BUSCA CNPJ ---
    const handleCnpjLookup = async () => {
        const cnpj = formData.cnpj.replace(/[^\dA-Za-z]/g, '');
        if (cnpj.length !== 14) {
            setCnpjMsg({ type: 'error', text: 'CNPJ deve ter 14 dígitos.' });
            return;
        }
        setCnpjLoading(true);
        setCnpjMsg(null);
        try {
            const res = await api.get(`/utils/cnpj/${cnpj}`);
            const d = res.data;
            setFormData(prev => ({
                ...prev,
                companyName: d.name || prev.companyName,
                ie: d.ie || 'ISENTO',
                phone: prev.phone || d.phone || '',
                financial_email: prev.financial_email || d.email || '',
                address: {
                    cep: d.address?.cep || prev.address.cep,
                    logradouro: d.address?.logradouro || prev.address.logradouro,
                    numero: d.address?.numero || prev.address.numero,
                    complemento: d.address?.complemento || prev.address.complemento,
                    bairro: d.address?.bairro || prev.address.bairro,
                    cidade: d.address?.cidade || prev.address.cidade,
                    uf: d.address?.uf || prev.address.uf,
                }
            }));
            setCnpjMsg({ type: 'success', text: `✅ Dados preenchidos: ${d.name}` });
        } catch (e) {
            setCnpjMsg({ type: 'error', text: 'CNPJ não encontrado ou inválido.' });
        } finally {
            setCnpjLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (!validations.email || !validations.doc || !validations.password) {
            setErrorMsg('Verifique os campos marcados em vermelho.');
            return;
        }

        setLoading(true);

        try {
            // --- MUDANÇA: Chama API Local em vez de Cloud Function ---
            await api.post('/register', {
                ...formData,
                planValue: totalPrice
            });

            setSuccess(true);
            
            // Redireciona para login após 3 segundos
            setTimeout(() => {
                setView('login');
            }, 3000);

        } catch (error) {
            console.error(error);
            // Captura mensagem de erro do backend
            const msg = error.response?.data?.error || 'Erro ao criar conta. Tente novamente ou contate o suporte.';
            setErrorMsg(msg);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-xl shadow-xl text-center max-w-md animate-in zoom-in">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Conta Criada com Sucesso!</h2>
                    <p className="text-gray-600 mb-6">
                        Sua empresa <strong>{formData.companyName}</strong> foi registrada.
                        <br/>Você será redirecionado para o login.
                    </p>
                    <button onClick={() => setView('login')} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold">
                        Ir para Login Agora
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4 md:p-8">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col md:flex-row overflow-hidden">
                
                {/* LADO ESQUERDO (INFO) */}
                <div className="bg-gray-50 p-8 md:w-2/5 border-r border-gray-100 hidden md:block">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                        <MessageSquare className="mr-2 text-blue-600"/> WA-Desk Local
                    </h2>
                    <div className="space-y-6">
                        <div className="flex items-start">
                            <Users className="w-6 h-6 text-blue-500 mr-3 mt-1"/>
                            <div>
                                <h4 className="font-bold text-gray-700">Multi-Atendentes</h4>
                                <p className="text-sm text-gray-500">Organize sua equipe em setores.</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <Calculator className="w-6 h-6 text-blue-500 mr-3 mt-1"/>
                            <div>
                                <h4 className="font-bold text-gray-700">Custo Fixo</h4>
                                <p className="text-sm text-gray-500">Sem surpresas no fim do mês.</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <FileText className="w-6 h-6 text-blue-500 mr-3 mt-1"/>
                            <div>
                                <h4 className="font-bold text-gray-700">Relatórios</h4>
                                <p className="text-sm text-gray-500">Auditoria completa de atendimentos.</p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-12 bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <p className="text-sm text-blue-800 italic">
                            "A melhor solução para centralizar seu WhatsApp em servidor próprio."
                        </p>
                    </div>
                </div>

                {/* LADO DIREITO (FORM) */}
                <div className="p-8 md:w-3/5 relative">
                    <button onClick={() => setView('login')} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                        <X className="w-6 h-6" />
                    </button>

                    <h2 className="text-2xl font-bold text-gray-800 mb-1">Contratar Plano</h2>
                    <p className="text-gray-500 mb-6 text-sm">Preencha os dados da empresa e do administrador.</p>

                    {errorMsg && (
                        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm font-medium border border-red-100">
                            {errorMsg}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Toggle PJ / PF */}
                        <div className="flex mb-5 rounded-lg overflow-hidden border border-gray-200 w-fit mx-auto md:mx-0">
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, tipo_pessoa: 'PJ' })}
                                className={`px-5 py-2 text-sm font-bold transition-colors ${formData.tipo_pessoa === 'PJ' ? 'bg-gray-800 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                            >
                                🏢 Pessoa Jurídica
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, tipo_pessoa: 'PF' })}
                                className={`px-5 py-2 text-sm font-bold transition-colors ${formData.tipo_pessoa === 'PF' ? 'bg-gray-800 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                            >
                                👤 Pessoa Física
                            </button>
                        </div>

                        {/* Feedback CNPJ */}
                        {cnpjMsg && (
                            <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
                                cnpjMsg.type === 'success'
                                    ? 'bg-green-50 text-green-700 border border-green-200'
                                    : 'bg-red-50 text-red-700 border border-red-200'
                            }`}>
                                {cnpjMsg.text}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* CPF ou CNPJ */}
                            {formData.tipo_pessoa === 'PJ' ? (
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">CNPJ</label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <input
                                                type="text" required
                                                placeholder="00.000.000/0001-00"
                                                className={`w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 outline-none ${validations.doc === false ? 'border-red-500 focus:ring-red-200' : 'focus:ring-blue-500'}`}
                                                value={formData.cnpj}
                                                onChange={e => { setFormData({ ...formData, cnpj: e.target.value }); setCnpjMsg(null); }}
                                            />
                                            {validations.doc && <Check className="w-4 h-4 text-green-500 absolute right-3 top-2.5" />}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCnpjLookup}
                                            disabled={cnpjLoading}
                                            title="Pesquisar CNPJ e preencher dados automaticamente"
                                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 flex items-center gap-1 text-sm font-semibold"
                                        >
                                            {cnpjLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                            Buscar
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">CPF</label>
                                    <div className="relative">
                                        <input
                                            type="text" required
                                            placeholder="000.000.000-00"
                                            className={`w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 outline-none ${validations.doc === false ? 'border-red-500 focus:ring-red-200' : 'focus:ring-blue-500'}`}
                                            value={formData.cpf}
                                            onChange={e => setFormData({ ...formData, cpf: e.target.value })}
                                        />
                                        {validations.doc && <Check className="w-4 h-4 text-green-500 absolute right-3 top-2.5" />}
                                    </div>
                                </div>
                            )}

                            {/* Nome / Razão Social */}
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                    {formData.tipo_pessoa === 'PJ' ? 'Razão Social' : 'Nome Completo'}
                                </label>
                                <input
                                    type="text" required
                                    className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.companyName}
                                    onChange={e => setFormData({ ...formData, companyName: e.target.value })}
                                />
                            </div>

                            {/* IE — somente PJ */}
                            {formData.tipo_pessoa === 'PJ' && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Inscrição Estadual</label>
                                    <input
                                        type="text"
                                        placeholder="ISENTO"
                                        className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.ie}
                                        onChange={e => setFormData({ ...formData, ie: e.target.value })}
                                    />
                                </div>
                            )}

                            {/* Telefone */}
                            <div className={formData.tipo_pessoa === 'PF' ? 'md:col-span-1' : ''}>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Telefone</label>
                                <input
                                    type="text" required
                                    placeholder="(00) 00000-0000"
                                    className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.phone}
                                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                />
                            </div>

                            {/* E-mail Financeiro */}
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">E-mail Financeiro (Opcional)</label>
                                <input
                                    type="email"
                                    placeholder="email@empresa.com"
                                    className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.financial_email}
                                    onChange={e => setFormData({ ...formData, financial_email: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Endereço */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                            <h4 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center">
                                <MapPin className="w-3 h-3 mr-1" /> Endereço
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div className="md:col-span-1">
                                    <input type="text" placeholder="CEP" className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={formData.address.cep} onChange={e => setFormData({ ...formData, address: { ...formData.address, cep: e.target.value } })} />
                                </div>
                                <div className="md:col-span-2">
                                    <input type="text" placeholder="Logradouro" className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={formData.address.logradouro} onChange={e => setFormData({ ...formData, address: { ...formData.address, logradouro: e.target.value } })} />
                                </div>
                                <div className="md:col-span-1">
                                    <input type="text" placeholder="Número" className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={formData.address.numero} onChange={e => setFormData({ ...formData, address: { ...formData.address, numero: e.target.value } })} />
                                </div>
                                <div className="md:col-span-2">
                                    <input type="text" placeholder="Complemento" className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={formData.address.complemento} onChange={e => setFormData({ ...formData, address: { ...formData.address, complemento: e.target.value } })} />
                                </div>
                                <div className="md:col-span-2">
                                    <input type="text" placeholder="Bairro" className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={formData.address.bairro} onChange={e => setFormData({ ...formData, address: { ...formData.address, bairro: e.target.value } })} />
                                </div>
                                <div className="md:col-span-3">
                                    <input type="text" placeholder="Cidade" className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={formData.address.cidade} onChange={e => setFormData({ ...formData, address: { ...formData.address, cidade: e.target.value } })} />
                                </div>
                                <div className="md:col-span-1">
                                    <input type="text" placeholder="UF" className="w-full p-2 border rounded bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={formData.address.uf} onChange={e => setFormData({ ...formData, address: { ...formData.address, uf: e.target.value } })} />
                                </div>
                            </div>
                        </div>


                        {/* Dados Admin */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Nome do Administrador</label>
                            <input 
                                type="text" required 
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.adminName}
                                onChange={e => setFormData({...formData, adminName: e.target.value})}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">E-mail de Acesso</label>
                                <input 
                                    type="email" required 
                                    className={`w-full p-2 border rounded focus:ring-2 outline-none ${validations.email === false ? 'border-red-500' : 'focus:ring-blue-500'}`}
                                    value={formData.adminEmail}
                                    onChange={e => setFormData({...formData, adminEmail: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Senha</label>
                                <div className="relative">
                                    <Lock className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
                                    <input 
                                        type="password" required 
                                        className={`w-full pl-8 p-2 border rounded focus:ring-2 outline-none ${validations.password === false ? 'border-red-500' : 'focus:ring-blue-500'}`}
                                        value={formData.adminPassword}
                                        onChange={e => setFormData({...formData, adminPassword: e.target.value})}
                                        placeholder="Min. 6 caracteres"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Seleção de Plano */}
                        <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 mt-4">
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-4">Selecione o Limite de Usuários</label>
                            
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-4">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({...formData, usersLimit: Math.max(BASE_USERS, formData.usersLimit - 1)})}
                                        disabled={formData.usersLimit <= BASE_USERS}
                                        className="w-10 h-10 rounded-full flex items-center justify-center bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                                    >
                                        <span className="text-xl font-bold leading-none">-</span>
                                    </button>
                                    
                                    <div className="text-center w-16">
                                        <span className="text-3xl font-bold text-blue-700 block leading-none">{formData.usersLimit}</span>
                                        <span className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">Usuários</span>
                                    </div>
                                    
                                    <button
                                        type="button"
                                        onClick={() => setFormData({...formData, usersLimit: formData.usersLimit + 1})}
                                        className="w-10 h-10 rounded-full flex items-center justify-center bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 shadow-sm transition-all text-blue-600 hover:text-blue-700"
                                    >
                                        <span className="text-xl font-bold leading-none">+</span>
                                    </button>
                                </div>
                                
                                <div className="text-right pl-6 border-l border-gray-200">
                                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Valor Mensal</p>
                                    <p className="text-3xl font-bold text-blue-700 tracking-tight">
                                        R$ {totalPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="text-xs text-gray-500 mt-4 bg-white p-3 rounded border border-gray-100 shadow-sm">
                                <span className="text-blue-600 font-bold mr-1">ℹ️</span>
                                O plano inicial inclui <strong>{BASE_USERS} usuários</strong> por R$ {BASE_PRICE.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}. Cada usuário adicional tem um custo de <strong>R$ {EXTRA_USER_PRICE.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</strong>.
                            </div>
                        </div>

                        <button type="submit" disabled={loading} className="w-full py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 shadow-lg flex justify-center items-center transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                            {loading ? <Loader2 className="animate-spin w-5 h-5 mr-2" /> : 'Finalizar Cadastro'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SignUpScreen;